import { Component, inject, Input, OnInit } from '@angular/core';
import { OptionsList } from 'gung-common';
import { GungCompany } from '../../../services/gung-company/gung-company.service';
import { GungNavbarService } from '../../../services/gung-navbar/gung-navbar.service';
import { CheckoutConfigService } from '../../../services/checkout-config/checkout-config.service';
import { CartListCheckoutConfigService } from '../../../services/cart-list-checkout-config/cart-list-checkout-config.service';

export type NavbarType = 'assortment' | 'flow';

export interface MyCompanyLayoutNavbar {
  type: NavbarType;
  assortmentMenuLevel?: number | 'auto';
  removeUserAssortmentTopLevel?: boolean;
  forceToNotShowFlowsOnTop?: boolean;
  hideMyCustomers?: boolean;
  hideFavorites?: boolean;
  hideCart?: boolean;
  hideSelectedCustomer?: boolean;
  showNavbarSearch?: boolean;
}

export interface MyCompanyLayoutCheckout {
  sideButtons?: boolean;
  enableDiscount?: boolean;
  enableOffers?: boolean;
  hideCreateQuotation?: boolean;
  enableGlobalDeliveryDate?: boolean;
  enableSalesEditPrice?: boolean;
  enableSalesEditDiscount?: boolean;
  userCanAddDescription?: boolean;
  enableCopyRow?: boolean;
  userCanChangeDeliveryDates?: boolean;
}

export interface MyCompanyLayout {
  maxWidth: number;
  excludedUrls: string[];
  navbar?: MyCompanyLayoutNavbar | null;
  checkout?: MyCompanyLayoutCheckout | null;
}

@Component({
  selector: 'lib-my-company-layout',
  templateUrl: './my-company-layout.component.html',
  styleUrls: ['./my-company-layout.component.css'],
  standalone: false
})
export class MyCompanyLayoutComponent implements OnInit {
  protected gungNavbarService = inject(GungNavbarService);
  protected checkoutConfigService = inject(CheckoutConfigService);
  protected cartListCheckoutConfigService = inject(CartListCheckoutConfigService);

  @Input()
  public company: GungCompany;

  public myCompanyLayout: MyCompanyLayout;

  showingCodeDefaults = false;
  showingCheckoutCodeDefaults = false;
  newUrl = '';

  navbarTypeOptions: OptionsList[] = [
    { id: 'assortment', name: 'Assortment' },
    { id: 'flow', name: 'Flow' }
  ];

  assortmentMenuLevelOptions: OptionsList[] = [
    { id: '0', name: '0' },
    { id: '1', name: '1' },
    { id: '2', name: '2' },
    { id: '3', name: '3' },
    { id: 'auto', name: 'auto' }
  ];

  get assortmentMenuLevelStr(): string {
    return String(this.myCompanyLayout?.navbar?.assortmentMenuLevel ?? '0');
  }

  set assortmentMenuLevelStr(val: string) {
    if (this.myCompanyLayout?.navbar) {
      this.myCompanyLayout.navbar.assortmentMenuLevel = val === 'auto' ? 'auto' : Number(val);
    }
  }

  ngOnInit(): void {
    if (!this.company.extra) {
      this.company.extra = {};
    }
    if (!this.company.extra.layout) {
      this.company.extra.layout = { maxWidth: null, excludedUrls: [], navbar: null };
    }
    this.myCompanyLayout = this.company.extra.layout;
    if (!this.myCompanyLayout.navbar) {
      this.myCompanyLayout.navbar = { type: this.gungNavbarService.isAssortmentBased ? 'assortment' : 'flow' };
    }
    this.applyNavbarDefaults(this.myCompanyLayout.navbar);
    if (!this.myCompanyLayout.checkout) {
      this.myCompanyLayout.checkout = {};
    }
    this.applyCheckoutDefaults(this.myCompanyLayout.checkout);
  }

  protected applyNavbarDefaults(navbar: MyCompanyLayoutNavbar): void {
    const svc = this.gungNavbarService;
    if (navbar.type == null) {
      navbar.type = svc.isAssortmentBased ? 'assortment' : 'flow';
    }
    const level = navbar.assortmentMenuLevel;
    if (level == null) {
      navbar.assortmentMenuLevel = svc.assortmentMenuLevel;
    } else if (level !== 'auto') {
      navbar.assortmentMenuLevel = Number(level);
    }
    if (navbar.removeUserAssortmentTopLevel == null) {
      navbar.removeUserAssortmentTopLevel = svc.removeUserAssortmentTopLevel;
    }
    if (navbar.forceToNotShowFlowsOnTop == null) {
      navbar.forceToNotShowFlowsOnTop = svc.forceToNotShowFlowsOnTop;
    }
    if (navbar.hideMyCustomers == null) {
      navbar.hideMyCustomers = svc.hideMyCustomers;
    }
    if (navbar.hideFavorites == null) {
      navbar.hideFavorites = svc.hideFavorites;
    }
    if (navbar.hideCart == null) {
      navbar.hideCart = svc.hideCart;
    }
    if (navbar.hideSelectedCustomer == null) {
      navbar.hideSelectedCustomer = svc.hideSelectedCustomer;
    }
    if (navbar.showNavbarSearch == null) {
      navbar.showNavbarSearch = svc.showNavbarSearch;
    }
  }

  protected applyCheckoutDefaults(checkout: MyCompanyLayoutCheckout): void {
    const svc = this.checkoutConfigService;
    if (checkout.sideButtons == null) {
      checkout.sideButtons = svc.sideButtons;
    }
    if (checkout.enableDiscount == null) {
      checkout.enableDiscount = svc.enableDiscount;
    }
    if (checkout.enableOffers == null) {
      checkout.enableOffers = svc.enableOffers;
    }
    if (checkout.hideCreateQuotation == null) {
      checkout.hideCreateQuotation = svc.hideCreateQuotation;
    }
    if (checkout.enableGlobalDeliveryDate == null) {
      checkout.enableGlobalDeliveryDate = this.cartListCheckoutConfigService.enableGlobalDeliveryDate;
    }
    if (checkout.enableSalesEditPrice == null) {
      checkout.enableSalesEditPrice = this.cartListCheckoutConfigService.enableSalesEditPrice;
    }
    if (checkout.enableSalesEditDiscount == null) {
      checkout.enableSalesEditDiscount = this.cartListCheckoutConfigService.enableSalesEditDiscount;
    }
    if (checkout.userCanAddDescription == null) {
      checkout.userCanAddDescription = this.cartListCheckoutConfigService.userCanAddDescription;
    }
    if (checkout.enableCopyRow == null) {
      checkout.enableCopyRow = this.cartListCheckoutConfigService.enableCopyRow;
    }
    if (checkout.userCanChangeDeliveryDates == null) {
      checkout.userCanChangeDeliveryDates = this.cartListCheckoutConfigService.userCanChangeDeliveryDates;
    }
  }

  addUrl() {
    const url = this.newUrl?.trim();
    if (url && !this.myCompanyLayout.excludedUrls.includes(url)) {
      this.myCompanyLayout.excludedUrls.push(url);
    }
    this.newUrl = '';
  }

  removeUrl(index: number) {
    this.myCompanyLayout.excludedUrls.splice(index, 1);
  }

  resetNavbarToDefaults() {
    this.myCompanyLayout.navbar = null;
    this.showingCodeDefaults = true;
  }

  customizeNavbar() {
    this.myCompanyLayout.navbar = { type: this.gungNavbarService.isAssortmentBased ? 'assortment' : 'flow' };
    this.applyNavbarDefaults(this.myCompanyLayout.navbar);
    this.showingCodeDefaults = false;
  }

  resetCheckoutToDefaults() {
    this.myCompanyLayout.checkout = null;
    this.showingCheckoutCodeDefaults = true;
  }

  customizeCheckout() {
    this.myCompanyLayout.checkout = {};
    this.applyCheckoutDefaults(this.myCompanyLayout.checkout);
    this.showingCheckoutCodeDefaults = false;
  }
}
